import { BenefitFormData } from '@/types/circuits/CircuitDaysFormData';
import { memo, useCallback, useMemo, useState } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useEstimatePrices } from '@/hooks/useEstimatePrices';
import { EstimateDayStepCardProps } from './EstimateDayStepCard';
import { SelectedEstimateOption } from '../../../types/estimates/SelectedEstimateOptionType';
import { useGetFileUrl } from '@/hooks/useFile';
import { useEstimateCarRental } from '@/hooks/useEstimateCarRental';
import EstimateCarRentalElement, {
  CarRentalElementType,
} from './EstimateCarRentalElement';
import EstimateDayStepCardTemplate from './EstimateDayStepCardTemplate';
import EstimateOptionalBenefitCell from './EstimateOptionalBenefitCell';
import EstimateOptionalCarRentalCategory from './EstimateOptionalCarRentalCategory';

interface EstimateDayCarRentalStepCardProps extends EstimateDayStepCardProps {
  nbAdults: number;
  nbChildren: number;
}

const EstimateDayCarRentalStepCard = ({
  step,
  date,
  dayIndex,
  computeOptionalBenefitPrice,
  selectedOptions,
  setSelectedOptions,
  currency = 'EUR',
  creationDate,
  accessToken,
  nbAdults,
  nbChildren,
  isViewMode = false,
  language,
}: EstimateDayCarRentalStepCardProps) => {
  const { type, computeBenefitPriceDelta, stepDates } = useEstimatePrices({
    step,
    computeOptionalBenefitPrice,
    date,
    dayIndex,
  });

  const defaultSelectedOption = useMemo(
    () =>
      Object.hasOwn(selectedOptions, step.id) ? selectedOptions[step.id] : null,
    [selectedOptions, step.id],
  );

  const optionalBenefitsWithDeltaPrice = useMemo(
    () =>
      step.optionalBenefits
        .map((benefit) => {
          const deltaPrice = computeBenefitPriceDelta(benefit);
          return { benefit, deltaPrice };
        })
        .toSorted((a, b) => a.deltaPrice - b.deltaPrice),
    [computeBenefitPriceDelta, step.optionalBenefits],
  );

  const carRentalCategories = useMemo(() => {
    const translations = language
      ? step.standardBenefit?.translations?.[language]
      : undefined;
    const translatedCategories = translations?.carRental?.carCategories ?? [];
    return (
      step.standardBenefit?.carRental?.carCategories
        .map((category, categoryIndex) => {
          if (!step.standardBenefit) return null;
          const benefitWithOnlyThisCategory = {
            ...step.standardBenefit,
            carRental: {
              carCategories: [category],
            },
          };
          const deltaPrice = computeBenefitPriceDelta(
            benefitWithOnlyThisCategory,
          );
          const translatedCategory = translatedCategories[categoryIndex];
          return {
            deltaPrice,
            category: {
              ...category,
              translations:
                language && translatedCategory
                  ? { [language]: translatedCategory }
                  : undefined,
            },
          };
        })
        .filter((v) => v != null)
        .toSorted((a, b) => a.deltaPrice - b.deltaPrice) ?? []
    );
  }, [computeBenefitPriceDelta, language, step.standardBenefit]);

  const carRentalStandardCategory = useMemo(
    () =>
      carRentalCategories.find(({ deltaPrice }) => deltaPrice === 0)?.category,
    [carRentalCategories],
  );

  const carRentalOptionalCategories = useMemo(() => {
    const indexLeastExpensiveCategory = carRentalCategories.findIndex(
      ({ deltaPrice }) => deltaPrice === 0,
    );
    return carRentalCategories.toSpliced(indexLeastExpensiveCategory, 1);
  }, [carRentalCategories]);

  const mainCategoryImages = useMemo(() => {
    return carRentalStandardCategory?.images ?? [];
  }, [carRentalStandardCategory?.images]);

  const mainCategoryImageKey = useMemo(() => {
    return mainCategoryImages.length > 0 ? mainCategoryImages[0] : null;
  }, [mainCategoryImages]);

  const { data: imageUrl } = useGetFileUrl(
    mainCategoryImageKey,
    false,
    accessToken,
  );

  const { carCategoryInformation } = useEstimateCarRental({
    carCategory: carRentalStandardCategory,
    originalCurrency: step.standardBenefit?.currency,
    currency,
    language,
  });

  const [selectedCarRentalCategoryId, setSelectedCarRentalCategoryId] =
    useState<string | null>(defaultSelectedOption?.carCategoryId ?? null);

  const [selectedOptionalBenefitId, setSelectedOptionalBenefitId] = useState<
    string | null
  >(defaultSelectedOption?.benefitId ?? null);

  const [displayOptionalBenefits, setDisplayOptionalBenefits] = useState(
    (selectedCarRentalCategoryId &&
      selectedCarRentalCategoryId !== carRentalStandardCategory?.id) ||
      !!selectedOptionalBenefitId,
  );

  const defaultSelection = useMemo(
    () =>
      `${selectedOptionalBenefitId ?? step.standardBenefit?.id ?? ''}-${selectedCarRentalCategoryId ?? ''}`,
    [
      selectedCarRentalCategoryId,
      selectedOptionalBenefitId,
      step.standardBenefit?.id,
    ],
  );

  const selectOptionalCategoryGenerator = useCallback(
    ({
      deltaPrice,
      benefit,
      category,
    }: {
      benefit?: BenefitFormData;
      category?: (typeof carRentalCategories)[number]['category'];
      deltaPrice: number;
    }) =>
      () => {
        setSelectedOptions((prev) => {
          if (benefit) {
            setSelectedCarRentalCategoryId(null);
            if (prev[step.id] && prev[step.id].benefitId === benefit.id) {
              setSelectedOptionalBenefitId(null);
              // oxlint-disable-next-line @typescript-eslint/no-unused-vars
              const { [step.id]: _, ...rest } = prev;
              return rest;
            }
            setSelectedOptionalBenefitId(benefit.id);
            return {
              ...prev,
              [step.id]: {
                benefitId: benefit.id,
                type,
                deltaPrice,
              } as SelectedEstimateOption,
            };
          }
          setSelectedOptionalBenefitId(null);
          if (
            prev[step.id] &&
            prev[step.id].benefitId === step.standardBenefit?.id &&
            prev[step.id].carCategoryId === category?.id
          ) {
            setSelectedCarRentalCategoryId(null);
            // oxlint-disable-next-line @typescript-eslint/no-unused-vars
            const { [step.id]: _, ...rest } = prev;
            return rest;
          }
          setSelectedCarRentalCategoryId(category?.id ?? null);
          return {
            ...prev,
            [step.id]: {
              benefitId: step.standardBenefit?.id,
              type,
              deltaPrice,
              carCategoryId: category?.id ?? undefined,
            } as SelectedEstimateOption,
          };
        });
      },
    [setSelectedOptions, step.id, step.standardBenefit?.id, type],
  );

  const commonShouldLimitInsurance = useMemo(
    () => step.standardBenefit?.carRental?.shouldLimitInsurance ?? undefined,
    [step.standardBenefit?.carRental?.shouldLimitInsurance],
  );

  const shouldLimitInsurance = useMemo(
    () =>
      (carRentalStandardCategory?.displayCategoryOptions ?? false)
        ? (carRentalStandardCategory?.shouldLimitInsurance ?? false)
        : (commonShouldLimitInsurance ?? false),
    [
      carRentalStandardCategory?.displayCategoryOptions,
      carRentalStandardCategory?.shouldLimitInsurance,
      commonShouldLimitInsurance,
    ],
  );

  const nbCars = useMemo(
    () =>
      Math.ceil(
        (nbAdults + nbChildren) / (carRentalStandardCategory?.maxCapacity ?? 4),
      ),
    [carRentalStandardCategory?.maxCapacity, nbAdults, nbChildren],
  );

  const getCategoryTranslations = useCallback(
    (elementType: CarRentalElementType) => {
      if (!carRentalStandardCategory || !language) return undefined;
      return carRentalStandardCategory?.translations?.[language]?.[elementType];
    },
    [carRentalStandardCategory, language],
  );

  const EstimateCarRentalElementMemo = useMemo(
    () =>
      memo(({ elementType }: { elementType: CarRentalElementType }) => (
        <EstimateCarRentalElement
          elementType={elementType}
          data={carRentalStandardCategory?.[elementType]}
          stepId={step.id}
          benefitId={step.standardBenefit?.id ?? ''}
          carCategoryId={carRentalStandardCategory?.id ?? ''}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          canCheckOnlyOneElement={
            elementType === 'insurances' && shouldLimitInsurance
          }
          nbDays={step.nbDays ?? 1}
          nbCars={nbCars}
          currency={currency}
          originalCurrency={step.standardBenefit?.currency}
          isMainCarCategory
          translations={getCategoryTranslations(elementType)}
          language={language}
        />
      )),
    [
      carRentalStandardCategory,
      currency,
      getCategoryTranslations,
      language,
      nbCars,
      selectedOptions,
      setSelectedOptions,
      shouldLimitInsurance,
      step.id,
      step.nbDays,
      step.standardBenefit?.currency,
      step.standardBenefit?.id,
    ],
  );

  const isStandardSelected = useMemo(
    () =>
      selectedCarRentalCategoryId === carRentalStandardCategory?.id ||
      ((selectedOptionalBenefitId === step.standardBenefit?.id ||
        selectedOptionalBenefitId === null) &&
        selectedCarRentalCategoryId === null),
    [
      selectedCarRentalCategoryId,
      carRentalStandardCategory?.id,
      selectedOptionalBenefitId,
      step.standardBenefit?.id,
    ],
  );

  const handleStandardBenefitClick = useCallback(() => {
    if (step.standardBenefit) {
      selectOptionalCategoryGenerator({
        benefit: step.standardBenefit,
        deltaPrice: 0,
        category: carRentalStandardCategory,
      })();
    }
  }, [
    carRentalStandardCategory,
    selectOptionalCategoryGenerator,
    step.standardBenefit,
  ]);

  const standardName = useMemo(() => {
    const translatedCategory =
      language && carRentalStandardCategory?.translations
        ? carRentalStandardCategory.translations[language]
        : undefined;
    return `${nbCars} × ${
      (translatedCategory ? translatedCategory.category : undefined) ??
      carRentalStandardCategory?.category ??
      ''
    } - ${
      (translatedCategory ? translatedCategory.model : undefined) ??
      carRentalStandardCategory?.model ??
      ''
    }`;
  }, [
    language,
    carRentalStandardCategory?.translations,
    carRentalStandardCategory?.category,
    carRentalStandardCategory?.model,
    nbCars,
  ]);

  return (
    <RadioGroup disabled={isViewMode} defaultValue={defaultSelection}>
      <EstimateDayStepCardTemplate
        step={step}
        type={type}
        stepDates={stepDates}
        standardName={standardName}
        displayOptionalBenefits={displayOptionalBenefits}
        setDisplayOptionalBenefits={setDisplayOptionalBenefits}
        generalBenefitInformation={carCategoryInformation}
        displayOptionalBenefitsPredicate={() =>
          carRentalOptionalCategories.length > 0
        }
        imageUrl={imageUrl?.url}
        standardBenefitOptions={
          <>
            <EstimateCarRentalElementMemo elementType="insurances" />
            <EstimateCarRentalElementMemo elementType="options" />
            <EstimateCarRentalElementMemo elementType="taxes" />
            <EstimateCarRentalElementMemo elementType="oneWayFees" />
          </>
        }
        currency={currency}
        accessToken={accessToken}
        radioGroupElement={
          <RadioGroupItem
            value={step.standardBenefit?.id ?? ''}
            onClick={handleStandardBenefitClick}
            checked={isStandardSelected}
          />
        }
      >
        {carRentalOptionalCategories.map(
          ({ category, deltaPrice }, indexCategory) => (
            <EstimateOptionalCarRentalCategory
              benefitId={step.standardBenefit?.id ?? ''}
              category={category}
              deltaPrice={deltaPrice}
              nbDays={step.nbDays}
              isChecked={selectedCarRentalCategoryId === category.id}
              shouldDisplayDescription={step.optionalDisplays.includes(
                'longDescription',
              )}
              selectOptionalBenefit={selectOptionalCategoryGenerator({
                category,
                deltaPrice,
              })}
              accessToken={accessToken}
              stepId={step.id}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              commonShouldLimitInsurance={commonShouldLimitInsurance}
              nbParticipants={nbAdults + nbChildren}
              language={language}
              currency={currency}
              creationDate={creationDate}
              originalCurrency={step.standardBenefit?.currency}
              key={indexCategory}
            />
          ),
        )}
        {optionalBenefitsWithDeltaPrice.map(
          ({ benefit, deltaPrice }, indexBenefit) => (
            <EstimateOptionalBenefitCell
              optionalDisplays={step.optionalDisplays}
              benefit={benefit}
              deltaPrice={deltaPrice}
              isChecked={selectedOptionalBenefitId === benefit.id}
              selectOptionalBenefit={selectOptionalCategoryGenerator({
                benefit,
                deltaPrice,
              })}
              nbParticipants={nbAdults + nbChildren}
              imagesKeys={benefit.VehicleWithChauffeur?.images ?? []}
              mainImageKey={
                benefit.VehicleWithChauffeur?.images?.[0] ?? undefined
              }
              language={language}
              currency={currency}
              creationDate={creationDate}
              accessToken={accessToken}
              key={`day-${dayIndex}-optionalBenefit-${indexBenefit}-${benefit.id}`}
            />
          ),
        )}
      </EstimateDayStepCardTemplate>
    </RadioGroup>
  );
};

export default EstimateDayCarRentalStepCard;
